/**
 * Sprint 30 · 5 top-level category hub landing pages that aggregate the
 * Sprint-28 home-décor subcategory pages into dedicated SEO landing pages
 * targeting procurement-intent keywords. Zero duplicate content: these
 * hubs are 100% new copy — intro, overview, aggregated cards linking to
 * the existing /categories/home-decor/<slug> pages.
 *
 * Shape: { id, title, metaDescription, h1, eyebrow, lede, primaryKeyword,
 *          secondaryKeywords, overview[], buyerQuestions[],
 *          subcategorySlugs[] (-> /categories/home-decor/<slug>),
 *          servicesH2, serviceLinks[], hero }
 */

export const CATEGORY_HUBS = [
  {
    id: "tabletop-dining",
    title: "Tabletop & Dining Sourcing from India | Asia Sourcing",
    metaDescription:
      "Tabletop and dining sourcing from India for international retailers and private-label brands. Coasters, placemats, serving trays, table runners, napkin rings and tabletop décor — supplier verification, QC and export documentation.",
    eyebrow: "Category · Tabletop & Dining · India",
    h1: "Tabletop & dining sourcing from India.",
    primaryKeyword: "Tabletop and dining sourcing India",
    secondaryKeywords: [
      "Tabletop décor manufacturers India",
      "Dining accessories wholesale India",
      "Private label tabletop India",
      "Serving tray sourcing India",
      "Table runner block print India",
    ],
    lede:
      "India is the deepest sourcing geography in the world for mixed-material tabletop and dining — brass from Moradabad, mango wood from Saharanpur, marble from Agra, hand-blown glass from Firozabad, block-print textiles from Jaipur, hand-woven jute from Panipat, all inside a 300-km corridor and a single QC chain. Asia Sourcing has run tabletop and dining programmes for retailers, DTC brands and hospitality specifiers for 23 years from our Moradabad base.",
    overview: [
      {
        h2: "What India does best in tabletop",
        body: [
          "Six categories carry the volume: coasters, placemats, serving trays, table runners, napkin rings and the broader tabletop-décor envelope (centerpieces, candle rings, napkin accessories). Each is a complete sourcing programme on our platform — brief → cluster recommendation → prototype → sampling → in-line and pre-shipment QC → export documentation → container loading.",
          "The commercial advantage is category depth per container: a mixed FCL regularly consolidates 15–30 SKUs across brass, marble, wood, glass and textile with one load plan, one QC report pack and one set of export documents. That consolidation discipline is what retailers call India's unfair advantage over single-material origins.",
        ],
      },
      {
        h2: "Materials, finishes and development",
        body: [
          "Standard materials: brass (etched, hammered, antiqued, nickel), marble (white, black, inlay from Agra), mango and acacia wood with brass or leather handles, mouth-blown glass, azo-free block-print cotton and linen, hand-woven jute and seagrass. Food-contact finishes are LFGB and FDA laboratory-certified per batch; wood oils are food-grade; textile colour-fastness tested at grade 4–5 under laboratory wash and crock protocols.",
          "Development cycle brief-to-PPS: 4–7 weeks for metal and wood, 6–8 weeks for marble, 4–5 weeks for textile. Custom tooling (etching plates, inlay designs, loom patterns) carries a one-time contribution; recurring production holds published FOB.",
        ],
      },
      {
        h2: "Buyer markets and compliance",
        body: [
          "US retailers import under CPSIA, Prop 65 and FDA food-contact rules; UK buyers under UK REACH, UKCA and the UK food-safe schedule; EU importers under REACH SVHC, LFGB and FSC chain-of-custody. We coordinate all three stacks factory-side and ship the laboratory certificate pack with every container. Default handoff is FOB Mundra or Nhava Sheva; FCA or DDP available on request.",
        ],
      },
    ],
    serviceLinks: [
      { to: "/services/product-development-india", label: "Product development & sampling" },
      { to: "/services/quality-control-india", label: "AQL 2.5 / 4.0 inspection" },
      { to: "/services/export-documentation-india", label: "Export documentation" },
      { to: "/services/buying-house-india", label: "Full buying-house programme" },
    ],
    subcategorySlugs: ["tabletop-decor", "coasters", "placemats", "serving-trays", "table-runners", "napkin-rings"],
    buyerQuestions: [
      ["What is the minimum order for a tabletop container from India?", "A 20-foot FCL mixed across 15–30 SKUs is the standard entry point. We consolidate SKUs across brass, marble, wood, glass and textile from the Moradabad-Agra-Saharanpur-Jaipur corridor into one load plan with one QC report pack."],
      ["Which clusters do you work with for tabletop sourcing?", "Moradabad (brass), Agra (marble and inlay), Saharanpur (wood), Firozabad (glass), Jaipur (block print, enamel, bone inlay), Panipat (jute and cotton weaves). All documented on our clusters pages with buyer visit protocols."],
      ["Can you deliver food-contact certification for the US and EU?", "Yes. LFGB (EU 1935/2004) and FDA laboratory certificates per batch and colour, plus Prop 65 heavy-metal screening on glazes, lacquers and inlaid materials. Certificates ship with the container."],
      ["How do you prevent breakage on marble and glass tabletop?", "Honeycomb inner wraps, corrugated separators and ISTA-3A drop-test certified master cartons. Container loading is supervised, photographed and signed per FCL — programmes we run clear sub-1 % in-transit breakage."],
    ],
    hero: "/assets/home-decor-hub-hero.jpg",
  },
  {
    id: "candles-home-fragrance",
    title: "Candles & Home Fragrance Sourcing from India | Asia Sourcing",
    metaDescription:
      "Private-label candles and home fragrance sourcing from India. Soy candles, scented glass, reed diffusers, incense, room sprays — IFRA-compliant development, CLP labelling, EN burn testing and export documentation.",
    eyebrow: "Category · Candles & Home Fragrance · India",
    h1: "Candles & home fragrance sourcing from India.",
    primaryKeyword: "Candle sourcing India",
    secondaryKeywords: [
      "Private label candles India",
      "Home fragrance manufacturers India",
      "Reed diffuser sourcing India",
      "Scented candles wholesale India",
      "IFRA-compliant candles India",
      "Soy candle private label India",
    ],
    lede:
      "India's candle and home-fragrance clusters around Delhi NCR and Moradabad supply soy, paraffin and blend-wax candles, reed diffusers, room sprays, potpourri and incense accessories to retailers and private-label brands worldwide — with IFRA-compliant fragrance development, CLP-ready labelling for the EU and EN 15493/15494 burn testing via accredited laboratories. Asia Sourcing manages fragrance briefs, vessel sourcing, laboratory batch certification, QC and export documentation.",
    overview: [
      {
        h2: "What a private-label candle programme really covers",
        body: [
          "Six categories span the home-fragrance envelope: filled scented candles in glass, pillar and taper candles, decorative candle holders, Moroccan and modern lanterns, reed diffusers with Fiber or rattan sticks, and incense holders and burners. Each ships with laboratory certification matched to the destination market.",
          "Fragrance development runs briefed by category and story: three fragrance directions, three wax blends, three vessels — refined to a validated powerburn of four hours against the signed PPS before production releases. The release gate is laboratory-signed, not visual.",
        ],
      },
      {
        h2: "Regulatory stack: IFRA, CLP, EN, ASTM",
        body: [
          "EU: CLP labels with UFI codes, Safety Data Sheets per fragrance, EN 15493 and EN 15494 burn-test reports via accredited laboratories. US: ASTM F2417 testing and Prop 65 evaluation per fragrance component. UK: UK REACH and UKCA where applicable. All three regulatory packs are coordinated factory-side; we never ship a container without the matching certificate pack.",
        ],
      },
      {
        h2: "MOQ, lead time and price bands",
        body: [
          "Entry MOQ for a private-label candle programme is 2,000 pieces split across 3–4 fragrances in standard glass vessels; custom vessel moulds start at 3,000 pieces per vessel plus tooling. Reed diffusers start at 1,000 pieces per fragrance. Lead time brief-to-PPS: 4–6 weeks; production: 6–8 weeks; container load: 2 weeks. FOB entry 200 g filled glass ranges $2.20–$4.50 depending on wax, fragrance load and vessel.",
        ],
      },
    ],
    serviceLinks: [
      { to: "/services/product-development-india", label: "Fragrance development & sampling" },
      { to: "/services/quality-control-india", label: "Batch certification & QC" },
      { to: "/services/export-documentation-india", label: "CLP, SDS & export documentation" },
      { to: "/services/buying-house-india", label: "Full private-label programme" },
    ],
    subcategorySlugs: ["candles", "candle-holders", "candlesticks", "lanterns", "home-fragrance", "reed-diffusers", "incense-holders"],
    buyerQuestions: [
      ["What is the MOQ for a private-label candle programme from India?", "2,000 pieces split across 3–4 fragrances in standard glass vessels is the entry point. Custom vessel moulds carry a one-time tooling contribution and start at 3,000 pieces per vessel."],
      ["Do you supply CLP labels and SDS for the EU?", "Yes. UFI code registration, Safety Data Sheets per fragrance and CLP labels in the destination language ship with every container. EN 15493 and EN 15494 burn-test reports are included."],
      ["Can you develop a bespoke fragrance?", "Yes. Brief → 3 fragrance directions → fine-tuning → 4-hour powerburn PPS test. Development runs 4–6 weeks against the signed brief."],
      ["Which wax formulations are available?", "Soy, paraffin, soy-paraffin blends, and on request rapeseed and coconut blends — selected per destination market and price band. All formulations are IFRA-compliant and laboratory-batch-certified."],
    ],
    hero: "/assets/home-decor-hub-hero.jpg",
  },
  {
    id: "garden-outdoor",
    title: "Garden & Outdoor Décor Sourcing from India | Asia Sourcing",
    metaDescription:
      "Garden and outdoor décor sourcing from India for the US and European garden markets. Hummingbird feeders, lanterns, planters, watering globes and outdoor accessories — supplier verification, QC and export documentation.",
    eyebrow: "Category · Garden & Outdoor · India",
    h1: "Garden & outdoor décor sourcing from India.",
    primaryKeyword: "Garden décor sourcing India",
    secondaryKeywords: [
      "Garden accessories wholesale India",
      "Hummingbird feeder manufacturers India",
      "Outdoor planters India",
      "Garden lanterns private label India",
      "Watering globes India",
    ],
    lede:
      "Garden and outdoor is a US-driven seasonal category — the backyard market orders in autumn for spring landing, which means PPS freeze by September and container loading by November. Asia Sourcing runs the full workflow from Moradabad metalware, Firozabad hand-blown glass and Khurja terracotta: hummingbird feeders, outdoor lanterns, planters, watering globes, bird feeders and outdoor accessories, with CPSIA material documentation and ISTA-3A drop-tested packing for e-commerce.",
    overview: [
      {
        h2: "The US spring garden calendar",
        body: [
          "The seasonal landing-page calendar is fixed: brief in January, PPS validation February–March, production April–August, container loading September–October for US retail landing November–February. Programmes planned backwards from retail reset date clear the shelf; programmes planned forward from factory capacity miss it.",
          "We hold spring-reset slots across our garden factories January–March — buyers who confirm by December lock calendar; later confirmations slip to the following season.",
        ],
      },
      {
        h2: "Leak testing is the product",
        body: [
          "A leaking hummingbird feeder destroys reviews — so every batch runs a 24-hour fill-and-store test, valve flow verification and freeze-cycle test on the glass-to-metal seam. Food-grade silicone gaskets; BPA-free valves; lead-free glazes and glass, laboratory-verified. Packing is drop-test certified for e-commerce (ISTA-3A).",
        ],
      },
      {
        h2: "Compliance for the US and EU garden markets",
        body: [
          "US: CPSIA material documentation, UPC labelling, English-language care instructions. EU: REACH SVHC on coatings, EN safety where applicable. All garden pieces that touch water carry food-grade material certificates per batch; outdoor coatings carry documented UV and salt-spray resistance (72–240 hours depending on price tier).",
        ],
      },
    ],
    serviceLinks: [
      { to: "/services/product-development-india", label: "Product development & sampling" },
      { to: "/services/quality-control-india", label: "Leak testing & AQL inspection" },
      { to: "/services/export-documentation-india", label: "CPSIA & export documentation" },
      { to: "/services/buying-house-india", label: "Full seasonal programme" },
    ],
    subcategorySlugs: ["hummingbird-feeders", "lanterns"],
    extraInternal: [
      { to: "/products/planters", label: "Planters" },
      { to: "/products/garden-decor", label: "Garden décor" },
      { to: "/products/watering-globes", label: "Watering globes" },
      { to: "/products/bird-feeders", label: "Bird feeders" },
      { to: "/products/outdoor-decorative-products", label: "Outdoor decorative products" },
    ],
    buyerQuestions: [
      ["When should US garden retailers place their India order?", "Confirm by December for a spring reset: PPS freeze March, production April–August, container loading September–October for US retail landing November–February. Later confirmations slip to the following season."],
      ["How do you leak-test glass hummingbird feeders?", "24-hour fill-and-store test, valve flow verification, freeze-cycle test on the glass-to-metal seam, and food-grade silicone gaskets validated per batch. Full certificate pack ships with the container."],
      ["Are coatings outdoor-rated?", "Yes — documented salt-spray resistance (72 h entry, 240 h premium), UV-stable powder coating and drained bottom constructions on outdoor lanterns and planters. Specification is per batch, not per product line."],
      ["What is the MOQ for garden feeders?", "300–500 pieces per design on hand-blown glass hummingbird feeders. Spare-part programmes for valves and gaskets order separately."],
    ],
    hero: "/assets/home-decor-hub-hero.jpg",
  },
  {
    id: "bathroom-wellness",
    title: "Bathroom & Wellness Accessories Sourcing from India | Asia Sourcing",
    metaDescription:
      "Bathroom and wellness accessories sourcing from India. Marble, brass and ceramic bathroom series — soap dispensers, tumblers, trays, storage — with corrosion and pump life-cycle testing, QC and export documentation.",
    eyebrow: "Category · Bathroom & Wellness · India",
    h1: "Bathroom & wellness accessories sourcing from India.",
    primaryKeyword: "Bathroom accessories sourcing India",
    secondaryKeywords: [
      "Marble bathroom accessories India",
      "Brass bathroom accessories India",
      "Private label bathroom accessories India",
      "Soap dispenser manufacturers India",
      "Bathroom storage wholesale India",
    ],
    lede:
      "Bathroom accessories sell at retail as coordinated series — soap dispenser, tumbler, soap dish, tray, storage — in a single material language. Asia Sourcing runs bathroom programmes from Agra (marble), Moradabad (brass) and Khurja (ceramic), with 72-hour salt-spray corrosion testing on metal pumps, 10,000-cycle pump life-cycle verification, moisture resistance on woven storage and a documented coordinate finish standard across every piece in the series.",
    overview: [
      {
        h2: "The series as the retail unit",
        body: [
          "Core series architecture: four pieces (soap dispenser, tumbler, soap dish, tray) at launch, plus optional extensions (waste bin, toilet brush holder, storage jars) that let retailers stretch the series across seasons — one finish standard, one QC plan. Hospitality variants add anti-slip bases, security caps on dispensers and refill instructions for housekeeping teams.",
        ],
      },
      {
        h2: "The pump decides 90 % of complaints",
        body: [
          "90 % of soap dispenser complaints are about the pump. We qualify every pump variant with a 10,000-cycle life test, ±0.2 ml dispense accuracy and a 30-day fill seal test before a design goes to series. Pumps are stainless 304 or PP; replacement pumps ship as a separate spare-part SKU.",
        ],
      },
      {
        h2: "Moisture, calcium and corrosion",
        body: [
          "Bathroom accessories live in permanent humidity: we specify calcium resistance on surfaces, 72-hour salt-spray on metal fittings, hydrophobic impregnation on marble and mould-inhibitor treatment plus ventilated construction on woven storage. All four tests run per batch, laboratory-documented.",
        ],
      },
    ],
    serviceLinks: [
      { to: "/services/product-development-india", label: "Series development & sampling" },
      { to: "/services/quality-control-india", label: "Pump life-cycle & corrosion testing" },
      { to: "/services/export-documentation-india", label: "Export documentation" },
      { to: "/services/buying-house-india", label: "Full series programme" },
    ],
    subcategorySlugs: ["bathroom-accessories", "soap-dispensers", "bathroom-storage"],
    buyerQuestions: [
      ["Can you develop a coordinated bathroom series?", "Yes — we develop as an open system: four core pieces at launch (dispenser, tumbler, soap dish, tray) plus optional extensions (waste bin, toilet brush, storage) under one finish standard and one QC plan, so retailers can stretch the series across seasons."],
      ["How do you qualify a soap-dispenser pump?", "10,000-cycle life-cycle test, ±0.2 ml dispense accuracy and a 30-day fill seal test per pump variant before the design releases to series. Stainless 304 or PP; replacement pumps ship as separate spare-part SKUs."],
      ["Are hospitality variants available?", "Yes — anti-slip bases, security caps on dispensers and housekeeping refill instructions, with the same pump-qualification plan as the retail series."],
      ["What corrosion testing do you run on bathroom metalware?", "72-hour salt-spray on brass and stainless fittings per batch, with laboratory-signed reports accompanying the container. Hydrophobic impregnation on marble is batch-documented."],
    ],
    hero: "/assets/home-decor-hub-hero.jpg",
  },
  {
    id: "seasonal-decor",
    title: "Seasonal Décor Sourcing from India | Christmas & Festive | Asia Sourcing",
    metaDescription:
      "Seasonal décor and Christmas sourcing from India for international retailers. Metal, glass and textile ornaments, tree décor and advent accessories — supplier verification, EN 71-3 testing, QC and export documentation.",
    eyebrow: "Category · Seasonal Décor · India",
    h1: "Seasonal décor sourcing from India.",
    primaryKeyword: "Christmas décor sourcing India",
    secondaryKeywords: [
      "Christmas ornaments manufacturers India",
      "Private label Christmas décor India",
      "Seasonal décor wholesale India",
      "Festive décor sourcing India",
      "Hand-blown glass ornaments India",
    ],
    lede:
      "India is the world's second-largest exporter of Christmas and seasonal décor — Moradabad metal figures and pendants, Firozabad hand-blown and hand-painted glass ornaments, Delhi NCR embroidered and felt textile pieces. Asia Sourcing runs seasonal programmes with a fixed-window calendar: PPS freeze February–March, production May–July, loading September for October retail landing.",
    overview: [
      {
        h2: "The fixed-window seasonal calendar",
        body: [
          "The season has a hard deadline: retail landing in October means container loading by September, QC in August, production May–July and PPS freeze in February or March. Programmes planned backwards from the retail reset date land clean; late confirmations slip to the following season.",
          "We consolidate seasonal programmes across 30–60 SKUs per container, mixing metal, glass and textile from four clusters into one load plan with one QC report pack.",
        ],
      },
      {
        h2: "Compliance for seasonal items",
        body: [
          "EN 71-3 for children-adjacent articles, lead-free lacquers and documented glitter and flock materials ship with each batch. US programmes carry CPSIA documentation and UL where electrified. Every batch includes accredited-laboratory certificates referenced to the signed PPS panel.",
        ],
      },
      {
        h2: "Breakage protection on glass ornaments",
        body: [
          "Glass tree décor only survives shipping with system packing: honeycomb inner wraps, 6-piece and 12-piece sales cartons, ISTA-3A drop testing per master carton. Programmes under our supervision clear sub-1 % in-transit breakage, photo-documented at container load.",
        ],
      },
    ],
    serviceLinks: [
      { to: "/services/product-development-india", label: "Seasonal development & sampling" },
      { to: "/services/quality-control-india", label: "EN 71-3 & AQL inspection" },
      { to: "/services/export-documentation-india", label: "Export documentation" },
      { to: "/services/buying-house-india", label: "Full seasonal programme" },
    ],
    subcategorySlugs: ["christmas-decor", "christmas-ornaments"],
    extraInternal: [
      { to: "/products/seasonal-decor", label: "Seasonal décor products" },
    ],
    buyerQuestions: [
      ["When do we need to confirm a Christmas programme?", "PPS freeze February or March, production May–July, container loading September for October retail landing. We plan backwards from your retail reset date — confirm by January for a comfortable calendar."],
      ["Are the lacquers and glitters EU-compliant?", "Yes — EN 71-3 for children-adjacent articles, lead-free lacquers and documented glitter and flock materials, all with accredited-laboratory certificates per batch. US programmes carry CPSIA documentation."],
      ["What is the MOQ for Christmas ornaments?", "1,000–2,000 pieces per design on mouth-blown glass; 500 per design on metal and felt. 6-piece and 12-piece sets count as one SKU with their own carton."],
      ["Can you develop a themed seasonal collection?", "Yes — colour palette, decor rules and packaging line as a package. Retailers adopt the collection or pick motifs from it; development runs 6 weeks brief-to-PPS."],
    ],
    hero: "/assets/home-decor-hub-hero.jpg",
  },
];

export const CATEGORY_HUB_IDS = CATEGORY_HUBS.map((h) => h.id);

export const getCategoryHub = (id) => CATEGORY_HUBS.find((h) => h.id === id);
